import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { api } from '../../api';

interface Falha { id: string; tipo: string; criado_em: string; tentativas: number; ultimo_erro: string | null; os_numero: string | null; produto: string | null; quantidade: string | null }
interface Alerta { id: string; tipo: string; entregue_em: string; os_numero: string | null; produto: string | null; alertas: string[]; saldo_apos: string | null }
interface Execucao { id: string; rotina: string; origem: string; iniciada_em: string; status: string; lidos: number; criados: number; vinculados: number; atualizados: number; sem_mudanca: number; avisos: number; erro: string | null }
interface Dados {
  configurado: boolean; envio_ligado: boolean; centro_padrao: number;
  conexao: { status: string; ultima_sync_em: string | null; ultimo_erro: string | null; ultima_completa: string | null } | null;
  produtos: number; veiculos: number; veiculos_ativos: number;
  fila: { pendentes: number; entregues_hoje: number; falhas: number; descartados: number };
  falhas: Falha[]; alertas: Alerta[]; execucoes: Execucao[];
}
interface Saude { ok: boolean; status: number; detalhe: string | null; versao: string | null }

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const NOME_EXEC: Record<string, string> = { rodando: 'Rodando', ok: 'OK', parcial: 'Com avisos', erro: 'Erro' };
const NOME_ROTINA: Record<string, string> = { catalogo: 'Catálogo', centros: 'Centros ↔ veículos' };
const NOME_TIPO: Record<string, string> = { 'estoque.aplicado': 'Consumo', 'estoque.devolvido': 'Devolução', 'estoque.retirado': 'Retirada do cliente', 'estoque.estorno': 'Estorno' };
const NOME_ALERTA: Record<string, string> = { saldo_negativo: 'saldo negativo', produto_inativo: 'produto inativo', centro_inativo: 'centro inativo' };

/**
 * EstoqueMaza = dono do saldo das peças. O Ops espelha o catálogo (a cada 15 min), liga cada carro ao centro dele
 * pela placa e manda as baixas das peças lançadas na OS. O interruptor do envio fica no servidor (ESTOQUE_MAZA_ENVIO).
 */
export function EstoqueMaza({ admin, verAvisos }: { admin: boolean; verAvisos: (id: string) => void }) {
  const [d, setD] = useState<Dados | null>(null);
  const [s, setS] = useState<Saude | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const carregar = useCallback(() => {
    api<Dados>('/integracoes/estoque-maza').then(setD).catch((e) => setErro(e.message));
    api<Saude>('/integracoes/estoque-maza/saude').then(setS).catch((e) => setS({ ok: false, status: 0, detalhe: e.message, versao: null }));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  async function sincronizar() {
    setErro(null); setOcupado('sinc');
    try { await api('/integracoes/estoque-maza/sincronizar', { metodo: 'POST', corpo: {} }); } catch (e) { setErro((e as Error).message); }
    finally { setOcupado(null); carregar(); }
  }
  async function reenviar(id: string) {
    setErro(null); setOcupado(id);
    try { await api(`/integracoes/estoque-maza/eventos/${id}/reenviar`, { metodo: 'POST' }); } catch (e) { setErro((e as Error).message); }
    finally { setOcupado(null); carregar(); }
  }

  return (
    <section className="cartao integracao">
      <header className="bloco-topo">
        <div>
          <h2>EstoqueMaza</h2>
          <p className="nota">
            O saldo das peças é do EstoqueMaza. O Ops copia o <strong>catálogo</strong> a cada 15 minutos (ligado pelo id do produto,
            não pelo código), liga cada <strong>carro ao centro</strong> dele pela placa e manda a <strong>baixa</strong> de cada peça
            lançada na OS. Peça sem carro sai do carro habitual do técnico; sem nenhum, do almoxarifado (centro {d?.centro_padrao ?? 1}).
          </p>
        </div>
        {admin && d?.configurado && (
          <button className="btn-fantasma" disabled={ocupado === 'sinc'} onClick={sincronizar}><RefreshCw size={15} aria-hidden /> {ocupado === 'sinc' ? 'Sincronizando…' : 'Sincronizar agora'}</button>
        )}
      </header>

      {erro && <p className="alerta" role="alert">{erro}</p>}
      {!d ? <p className="nota">Carregando…</p> : !d.configurado ? (
        <p className="alerta alerta-aviso">EstoqueMaza não configurado neste servidor (falta <code>/etc/vivium-ops/estoque-maza.env</code>).</p>
      ) : (
        <>
          <div className="rolagem-x"><table className="tabela">
            <tbody>
              <tr><th>Conexão</th><td>
                {!s ? 'conferindo…' : s.ok ? <span className="situacao s-ok">Respondendo{s.versao ? ` (${s.versao})` : ''}</span>
                  : <><span className="situacao s-nok">Sem resposta</span><small>{s.detalhe}</small></>}
                {d.conexao?.ultimo_erro && <small>Último erro: {d.conexao.ultimo_erro}</small>}
              </td></tr>
              <tr><th>Última sincronização</th><td>{d.conexao?.ultima_sync_em ? dataHora(d.conexao.ultima_sync_em) : '—'}{d.conexao?.ultima_completa && <small>leitura completa do catálogo: {dataHora(d.conexao.ultima_completa)}</small>}</td></tr>
              <tr><th>Produtos ligados</th><td>{d.produtos}</td></tr>
              <tr><th>Veículos ligados a centro</th><td>{d.veiculos} de {d.veiculos_ativos} ativos</td></tr>
              <tr><th>Envio das baixas</th><td>
                {d.envio_ligado ? <span className="situacao s-ok">Ligado</span> : <span className="situacao s-na">Desligado</span>}
                <small>{d.envio_ligado ? 'As baixas vão para o EstoqueMaza a cada 2 minutos.' : 'As baixas ficam guardadas na fila até o go-live (ligado no servidor, não por esta tela).'}</small>
              </td></tr>
              <tr><th>Fila de baixas</th><td>{d.fila.pendentes} pendente(s) · {d.fila.entregues_hoje} entregue(s) hoje · {d.fila.falhas} com falha · {d.fila.descartados} descartada(s)</td></tr>
            </tbody>
          </table></div>

          {d.falhas.length > 0 && (
            <>
              <h3>Baixas com falha</h3>
              <div className="rolagem-x"><table className="tabela">
                <thead><tr><th>OS</th><th>Movimento</th><th>Motivo</th>{admin && <th aria-label="Ações" />}</tr></thead>
                <tbody>
                  {d.falhas.map((f) => (
                    <tr key={f.id}>
                      <td>{f.os_numero ?? '—'}<small>{dataHora(f.criado_em)}</small></td>
                      <td>{NOME_TIPO[f.tipo] ?? f.tipo}{f.produto && <small>{f.quantidade} × {f.produto}</small>}</td>
                      <td><small>{f.ultimo_erro ?? '—'}</small></td>
                      {admin && <td className="acoes"><button className="btn-fantasma" disabled={ocupado === f.id} onClick={() => reenviar(f.id)}><RotateCcw size={15} aria-hidden /> Reenviar</button></td>}
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}

          {d.alertas.length > 0 && (
            <>
              <h3>Entregues com alerta</h3>
              <p className="nota">A baixa entrou no EstoqueMaza e ficou marcada para o almoxarifado conciliar (relatório "Divergências da integração").</p>
              <div className="rolagem-x"><table className="tabela">
                <thead><tr><th>OS</th><th>Movimento</th><th>Alerta</th></tr></thead>
                <tbody>
                  {d.alertas.map((a) => (
                    <tr key={a.id}>
                      <td>{a.os_numero ?? '—'}<small>{dataHora(a.entregue_em)}</small></td>
                      <td>{NOME_TIPO[a.tipo] ?? a.tipo}{a.produto && <small>{a.produto}</small>}</td>
                      <td><span className="situacao s-nok">{a.alertas.map((x) => NOME_ALERTA[x] ?? x).join(', ')}</span>{a.saldo_apos != null && <small>saldo depois: {a.saldo_apos}</small>}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}

          {d.execucoes.length > 0 && (
            <>
              <h3>Histórico</h3>
              <div className="rolagem-x"><table className="tabela">
                <thead><tr><th>Quando</th><th>Rotina</th><th>Situação</th><th className="num">Lidos</th><th className="num">Novos</th><th className="num">Atualizados</th><th className="num">Avisos</th></tr></thead>
                <tbody>
                  {d.execucoes.map((e) => (
                    <tr key={e.id}>
                      <td>{dataHora(e.iniciada_em)}<small>{e.origem === 'manual' ? 'manual' : 'automática'}</small></td>
                      <td>{NOME_ROTINA[e.rotina] ?? e.rotina}</td>
                      <td><span className={`situacao ${e.status === 'ok' ? 's-ok' : e.status === 'rodando' ? 's-na' : 's-nok'}`}>{NOME_EXEC[e.status]}</span>{e.erro && <small>{e.erro}</small>}</td>
                      <td className="num">{e.lidos}</td>
                      <td className="num">{e.criados + e.vinculados}</td>
                      <td className="num">{e.atualizados}</td>
                      <td className="num">{e.avisos > 0 ? <button className="btn-link" onClick={() => verAvisos(e.id)}>{e.avisos}</button> : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
              <p className="nota">Avisos: código (SKU) em conflito com peça cadastrada à mão, centro de veículo sem carro no Ops, carro do Ops sem centro.</p>
            </>
          )}
        </>
      )}
    </section>
  );
}
